import { MutableRefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import { featureCollection } from '@turf/turf';
import useInitializeMapEffect from '../../hooks/useInitializeMapEffect';
import useInitializeDrawModeEffect from './hooks/useInitializeDrawModeEffect';
import MapButton from './buttons/MapButton';
import AnalyzeRegionButton from './buttons/AnalyzeRegionButton';
import ImportParcelsButton from './buttons/ImportParcelsButton';
import SoilDataCard from './SoilDataCard';
import loadSoilBoundaries from '../../utils/loadSoilBoundaries';
import useColorSoilLayer from '../../hooks/useColorSoilLayer';
import { showError } from '../../utils/toast';
import ChevronIcon from '../icons/ChevronIcon';
import { useLandAdvisorContext } from '../../contexts/LandAdvisorContext';
import landAdvisorModes from '../../constants/landAdvisorMode';
import useLandAdvisorMapState from './hooks/useLandAdvisorMapState';
import { CONUS_CENTER } from '../../constants/coords';
import LandAdvisorLocationSearch from './LandAdvisorLocationSearch';

const INIT_ZOOM = 3;
const STD_ZOOM = 14;

const PARCELS_SOURCE_ID = 'parcels';
const PARCELS_LAYER_ID = 'parcels-layer';
const PARCELS_OUTLINE_LAYER_ID = 'parcels-outline-layer';
const SOILS_SOURCE_ID = 'soils';
const SOILS_LAYER_ID = 'soils-layer';
const SOILS_SYMBOL_LAYER_ID = 'soils-symbol-layer';

const PaneContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const MapContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const OverlayContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const TopLeft = styled.div`
  position: absolute;
  top: 2rem;
  left: 2rem;
  right: 2rem;
  z-index: 2;
  pointer-events: auto;
  display: block;
  width: auto;
`;

const BottomButtonRow = styled.div`
  position: absolute;
  left: 2rem;
  right: 4.5rem;
  bottom: 2rem;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  z-index: 3;
  pointer-events: auto;
  flex-wrap: wrap;
  align-items: center;
`;

const AnalysisModeButton = styled(MapButton)`
  font-weight: 600;
  background: #e9eef5;
  color: #2d3a4a;
  border: 1.5px solid #b3c2d1;
`;

const onLoad = (mapRef: MutableRefObject<mapboxgl.Map | null>) => {
  const map = mapRef.current;
  if (!map) return;
  if (!map.getSource(PARCELS_SOURCE_ID)) {
    map.addSource(PARCELS_SOURCE_ID, {
      type: 'geojson',
      data: featureCollection([]),
    });
  }
  if (!map.getSource(SOILS_SOURCE_ID)) {
    map.addSource(SOILS_SOURCE_ID, {
      type: 'geojson',
      data: featureCollection([]),
    });
  }

  // Add layers to display parcels if they don't already exist
  if (!map.getLayer(PARCELS_LAYER_ID)) {
    map.addLayer({
      id: PARCELS_LAYER_ID,
      type: 'fill',
      source: PARCELS_SOURCE_ID,
      paint: {
        'fill-color': '#4fb1db',
        'fill-opacity': 0.4,
      },
    });
  }
  if (!map.getLayer(PARCELS_OUTLINE_LAYER_ID)) {
    map.addLayer({
      id: PARCELS_OUTLINE_LAYER_ID,
      type: 'line',
      source: PARCELS_SOURCE_ID,
      paint: {
        'line-color': '#4fb1db',
        'line-width': 2.5,
      },
    });
  }
  if (!map.getLayer(SOILS_LAYER_ID)) {
    map.addLayer({
      id: SOILS_LAYER_ID,
      type: 'fill',
      source: SOILS_SOURCE_ID,
      paint: {
        'fill-color': [
          'case',
          ['!=', ['get', 'rgbString'], null],
          ['get', 'rgbString'],
          '#926829',
        ],
        'fill-opacity': 0.6,
      },
    });
  }
  if (!map.getLayer(SOILS_SYMBOL_LAYER_ID)) {
    map.addLayer({
      id: SOILS_SYMBOL_LAYER_ID,
      type: 'symbol',
      source: SOILS_SOURCE_ID,
      layout: {
        'text-field': ['get', 'mapUnitSymbol'],
        'symbol-placement': 'point',
        'text-allow-overlap': true,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 10,
      },
      paint: {
        'text-halo-color': 'rgba(0,0,0,1)',
        'text-halo-width': 2,
        'text-color': '#fff',
      },
    });
  }
};

const onUnload = (mapRef: MutableRefObject<mapboxgl.Map | null>) => {
  const map = mapRef.current;
  if (!map) return;

  if (map.getLayer(PARCELS_LAYER_ID)) {
    map.removeLayer(PARCELS_LAYER_ID);
  }
  if (map.getLayer(PARCELS_OUTLINE_LAYER_ID)) {
    map.removeLayer(PARCELS_OUTLINE_LAYER_ID);
  }
  if (map.getSource(PARCELS_SOURCE_ID)) {
    map.removeSource(PARCELS_SOURCE_ID);
  }
  if (map.getLayer(SOILS_LAYER_ID)) {
    map.removeLayer(SOILS_LAYER_ID);
  }
  if (map.getLayer(SOILS_SYMBOL_LAYER_ID)) {
    map.removeLayer(SOILS_SYMBOL_LAYER_ID);
  }
  if (map.getSource(SOILS_SOURCE_ID)) {
    map.removeSource(SOILS_SOURCE_ID);
  }
};

const LandAdvisorRightPane = ({ leftPercent }: { leftPercent: number }) => {
  const {
    mode,
    setMode,
    parcels,
    setParcels,
    soilAttributes: { resetSoilAttributes, ...soilAttributes },
    soilPolygons,
    setSoilPolygons,
  } = useLandAdvisorContext();
  const { mapRef, mapLoaded, setMapLoaded, mapCenter } = useLandAdvisorMapState(
    STD_ZOOM,
  );
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const isDrawMode = mode === landAdvisorModes.DRAW;
  const isAnalysisMode = mode === landAdvisorModes.ANALYSIS;

  useInitializeMapEffect({
    mapContainer,
    mapRef,
    mapLoaded,
    setMapLoaded,
    onLoad,
    onUnload,
    lat: CONUS_CENTER.lat,
    lng: CONUS_CENTER.lng,
    zoom: INIT_ZOOM,
    controls: {
      geolocation: false,
      compass: false,
      fullscreen: false,
      navigationLocation: 'bottom-right',
    },
  });

  const { drawRef, enterDrawMode, exitDrawMode } = useInitializeDrawModeEffect({
    features: parcels,
    setFeatures: setParcels,
    mapLoaded,
    mapRef,
    onEnter: () => setMode(landAdvisorModes.DRAW),
    onExit: () => setMode(landAdvisorModes.VIEW),
  });

  const coloredSoilLayer = useColorSoilLayer({
    colorKey: soilAttributes.colorKey,
    soilLayer: { data: soilPolygons },
  });

  // Resize map when leftPercent changes
  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      mapRef.current.resize();
    }
  }, [leftPercent, mapLoaded, mapRef]);

  // Update drawn features source if state changes
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const source = mapRef.current.getSource(PARCELS_SOURCE_ID) as GeoJSONSource;
    if (source) {
      const data = featureCollection(isAnalysisMode && !loading ? [] : parcels);
      source.setData(data);
    }
  }, [parcels, isAnalysisMode, loading, mapLoaded, mapRef]);

  // Update soil if soilPolygons or mapLoaded state changes
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const source = mapRef.current.getSource(SOILS_SOURCE_ID) as GeoJSONSource;
    if (source) {
      const data =
        isAnalysisMode && !loading
          ? coloredSoilLayer?.data || featureCollection([])
          : featureCollection([]);
      source.setData(data);
    }
  }, [coloredSoilLayer, isAnalysisMode, loading, mapLoaded, mapRef]);

  const enterAnalysisMode = async () => {
    setMode(landAdvisorModes.ANALYSIS);
    // Clear previous data when entering analysis mode
    resetSoilAttributes();
    if (!parcels.length) return;
    setLoading(true);
    try {
      // Load basic information about soil boundaries
      const soilBoundaries = await loadSoilBoundaries(
        featureCollection(parcels),
      );
      setSoilPolygons(soilBoundaries);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteParcels = () => {
    if (!drawRef.current) return;

    const selected = drawRef.current.getSelectedIds();
    if (selected && selected.length > 0) {
      drawRef.current.delete(selected);
    } else {
      drawRef.current.deleteAll();
    }
    setParcels(drawRef.current.getAll()?.features || []);
  };

  return (
    <PaneContainer>
      <MapContainer ref={mapContainer} />
      <OverlayContainer>
        <TopLeft>
          {isAnalysisMode ? (
            <MapButton onClick={() => setMode(landAdvisorModes.VIEW)}>
              <ChevronIcon className="my-1" direction="left" />
            </MapButton>
          ) : (
            <LandAdvisorLocationSearch mapRef={mapRef} zoom={STD_ZOOM} />
          )}
        </TopLeft>
        {mapCenter && mapCenter !== CONUS_CENTER && (
          <>
            {!isAnalysisMode && (
              <BottomButtonRow>
                {isDrawMode ? (
                  <>
                    <MapButton onClick={deleteParcels}>
                      Delete parcels
                    </MapButton>
                    <MapButton onClick={exitDrawMode}>Done</MapButton>
                  </>
                ) : (
                  <>
                    <ImportParcelsButton
                      mapRef={mapRef}
                      parcels={parcels}
                      setParcels={setParcels}
                    />
                    <MapButton onClick={enterDrawMode}>
                      {parcels.length ? 'Edit' : 'Draw'} parcels
                    </MapButton>
                    <AnalyzeRegionButton
                      mapCenter={mapCenter}
                      drawRef={drawRef}
                      parcels={parcels}
                      setParcels={setParcels}
                    />
                    <AnalysisModeButton
                      disabled={!parcels?.length}
                      onClick={() => enterAnalysisMode()}
                    >
                      Analyze parcels
                    </AnalysisModeButton>
                  </>
                )}
              </BottomButtonRow>
            )}
            {isAnalysisMode && parcels.length > 0 && (
              <SoilDataCard
                data={soilPolygons}
                loading={loading}
                {...soilAttributes}
              />
            )}
          </>
        )}
      </OverlayContainer>
    </PaneContainer>
  );
};

export default LandAdvisorRightPane;
